import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngxs/store';
import { catchError, filter, iif, of, switchMap, take, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router, RouterModule } from '@angular/router';
import { UserSelectors } from '../../states/user/user-selector';
import { MyInstitutesSelectors } from '../../states/myInstitutes/my-institute-selector';
import { MyInstituteService } from '../../services/institute/myinstitute/my-institute.service';
import { Institute } from '../../models/institute/randomInstituteResponse';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InstituteService } from '../../services/institute/institute.service';
import { StudentDirectApplicationService } from '../../services/student-direct-application/student-direct-application.service';
import { ClarityCallPopupComponent } from '../../shared/components/clarity-call-popup/clarity-call-popup.component';

export interface DirectApplyInstitute {
  id: number;              // StudentDirectApplication.Id (for DELETE)
  instituteId: string;
  instituteName: string;
  courseName?: string | null;
  inTakeYear?: string | null;
  mentorDeadline?: string | null;
  logo?: string;
  courseLevel?: number | null;
  courseLevelName?: string | null;
  courseId?: number | null;
  status?: number;
  isPremiumApply?: boolean;
  milestones?: any[];
}

@Component({
  selector: 'app-my-colleges',
  standalone: true,
  imports: [CommonModule, ToastModule, ProgressSpinnerModule, RouterModule, ClarityCallPopupComponent],
  templateUrl: './my-colleges.component.html',
  styleUrl: './my-colleges.component.scss',
  providers: [MessageService]
})
export class MyCollegesComponent implements OnInit {
  showClarityPopup = false;
  gridColleges: Institute[] = [];

  // Direct Apply section — mirrors MyInstitutes data
  directApplyColleges: DirectApplyInstitute[] = [];
  directApplicationId: number | null = null;

  user: any;
  isLoading = true;
  isDirectApplyLoading = false;
  hasAssignedMentor = false;

  constructor(
    private store: Store,
    private router: Router,
    private myInstituteService: MyInstituteService,
    private studentDirectApplicationService: StudentDirectApplicationService,
    private messageService: MessageService,
    private instituteService: InstituteService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const accountStr = localStorage.getItem('account');
    if (accountStr) {
      const account = JSON.parse(accountStr);
      this.hasAssignedMentor = !!account?.hasAssignedMentor;
    }

    this.getMyInstitutes();
    // loadCurrentStudentDirectApplication is kept for when a directApplicationId exists
    if (!this.hasAssignedMentor) {
      this.loadCurrentStudentDirectApplication();
    }
  }

  // ─── My Colleges grid (same pattern as app-checklist) ───────────────────────

  getMyInstitutes() {
    this.store.select(UserSelectors.getUser).pipe(
      filter(user => !!user?.id),
      tap(user => (this.user = user)),
      switchMap(() =>
        this.store.select(MyInstitutesSelectors.getMyInstitute).pipe(
          take(1),
          switchMap(institutes =>
            iif(
              () => institutes?.items?.length > 0,
              of({
                institutes: {
                  ...institutes,
                  items: institutes.items.map((item: any) => ({
                    ...item,
                    inTakeYear: item.inTakeYear ?? null,
                    courseLevel: item.courseLevel != null ? Number(item.courseLevel) : null,
                  }))
                }
              }),
              this.myInstituteService.getMyInstitutes().pipe(
                map((response: any) => {
                  const items = response.result?.items || response.result || response;
                  return items.map((item: any) => ({
                    ...item,
                    inTakeYear: item.inTakeYear ?? null,
                    courseLevel: item.courseLevel != null ? Number(item.courseLevel) : null,
                    myInstituteId: item.myInstituteId ?? null,
                    courseId: item.courseId ?? null,
                  }));
                }),
                map(apiInstitutes => ({
                  institutes: { items: apiInstitutes }
                }))
              )
            )
          )
        )
      )
    ).subscribe({
      next: ({ institutes }) => {
        this.gridColleges = institutes?.items || [];
        this.isLoading = false;
        // Enrich each college card with full details from the institute API
        this.gridColleges.forEach((college, idx) => {
          this.instituteService.instituteDetailSearch({ name: college.instituteName, pageNumber: 1, pageSize: 1 }).subscribe(res => {
            const inst = res?.result?.[0];
            if (inst) {
              // Replace the object reference so Angular's change detection picks up the update
              this.gridColleges[idx] = {
                ...this.gridColleges[idx],
                qsRanking: inst.qsRanking ?? college.qsRanking,
                duration: inst.duration || college.duration,
                applicationDeadlineRegularDecision: inst.applicationDeadlineRegularDecision || college.applicationDeadlineRegularDecision,
                averageTutionFees: inst.averageTutionFees || college.averageTutionFees,
                currency: inst.currency || college.currency,
                coverImage: inst.coverImage || inst.image || college.coverImage,
                logo: inst.logo || college.logo,
                courseName: inst.courseName || college.courseName,
              };
              this.cdr.markForCheck();
            }
          });
        });
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  removeCollege(index: number) {
    const college = this.gridColleges[index];
    if (college && college.id && this.user?.id) {
      this.myInstituteService.removeMyInstitute(String(college.id), this.user.id).subscribe({
        next: () => {
          this.gridColleges.splice(index, 1);
          this.messageService.add({ severity: 'success', summary: 'Removed', detail: 'College removed successfully.' });
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to remove college.' });
        }
      });
    } else {
      this.gridColleges.splice(index, 1);
    }
  }

  /** Navigate to college search so user can add more colleges */
  browseColleges() {
    this.router.navigate(['/portal', 'institute-search'], { queryParams: { tab: 'universities', isDAPartner: true } });
  }

  /** Navigate to college search with no specific partner filter */
  browseAllColleges() {
    this.router.navigate(['/portal', 'institute-search'], { queryParams: { tab: 'universities' } });
  }

  /** Navigate to the Direct Apply landing/flow page */
  applyNow() {
    this.router.navigate(['/portal', 'direct-apply']);
  }

  // ─── Direct Apply section — StudentDirectApplication API ────────────────────

  /**
   * On init: check localStorage for a stored directApplicationId for this user,
   * and if found, load the direct apply colleges from the API.
   */
  loadCurrentStudentDirectApplication() {
    this.store.select(UserSelectors.getUser).pipe(
      filter(user => !!user?.id),
      take(1)
    ).subscribe(user => {
      this.studentDirectApplicationService
        .getByStudentId({ studentId: user.id })
        .pipe(catchError(() => of(null)))
        .subscribe((response: any) => {
          let id = null;
          if (Array.isArray(response) && response.length > 0) {
            id = response[0].id;
          } else if (response?.result) {
            id = Array.isArray(response.result) ? response.result[0]?.id : response.result.id;
          } else if (response?.id) {
            id = response.id;
          }

          if (id) {
            this.loadDirectApplyColleges(id);
          }
        });
    });
  }

  /**
   * Loads all institutes for a given StudentDirectApplication.
   * Called with the directApplicationId (the PK from POST or a stored value).
   */
  loadDirectApplyColleges(directApplicationId: number) {
    this.isDirectApplyLoading = true;
    this.directApplicationId = directApplicationId;

    this.studentDirectApplicationService.getByStudentId({ studentId: this.user.id })
      .pipe(catchError(() => of(null)))
      .subscribe({
        next: (response: any) => {
          let rawItems: any[] = [];
          if (Array.isArray(response)) {
            rawItems = response;
          } else if (response?.result) {
            rawItems = Array.isArray(response.result) ? response.result : [response.result];
          } else if (response && response.instituteId) {
            rawItems = [response];
          }

          this.directApplyColleges = rawItems.map((item: any): DirectApplyInstitute => {
            const enriched = this.gridColleges.find(g => String(g.id) === String(item.instituteId));
            return {
              id: item.id ?? 0,
              instituteId: String(item.instituteId ?? item.id ?? ''),
              instituteName: item.instituteName ?? enriched?.instituteName ?? 'Loading...',
              courseName: item.courseName ?? enriched?.courseName ?? null,
              inTakeYear: item.inTakeYear ?? enriched?.inTakeYear ?? null,
              mentorDeadline: enriched?.mentorDeadline ?? null,
              logo: enriched?.logo ?? '',
              courseLevel: item.courseLevel ?? enriched?.courseLevel ?? null,
              courseLevelName: item.courseLevelName ?? null,
              courseId: item.courseId ?? enriched?.courseId ?? null,
              status: item.status ?? 0,
              isPremiumApply: item.isPremiumApply ?? false,
              milestones: item.milestones ?? []
            };
          });

          // Fetch details for any that don't have an instituteName or courseName
          this.directApplyColleges.forEach((da, idx) => {
            const needsInstitute = da.instituteName === 'Loading...' || !da.instituteName || !da.logo;
            const needsCourse = da.courseId && !da.courseName;

            if (needsInstitute || needsCourse) {
              this.instituteService.getInstitute(da.instituteId).subscribe(inst => {
                if (inst) {
                  const resolvedName = inst.instituteName && inst.instituteName.trim() !== '' ? inst.instituteName : da.instituteName;
                  this.directApplyColleges[idx] = {
                    ...this.directApplyColleges[idx],
                    instituteName: resolvedName,
                    logo: inst.logo || da.logo,
                    courseName: da.courseName || inst.courseName || null,
                  };
                  this.cdr.markForCheck();

                  // Now that we have the resolved name, search for the course
                  if (needsCourse && resolvedName && resolvedName !== 'Loading...') {
                    const req = {
                      name: resolvedName,
                      pageNumber: 1,
                      pageSize: 500
                    };
                    this.instituteService.searchCourses(req).subscribe(res => {
                      const course = res.result?.find((c: any) => Number(c.id) === Number(da.courseId));
                      if (course) {
                        this.directApplyColleges[idx] = {
                          ...this.directApplyColleges[idx],
                          courseName: course.courseName
                        };
                        this.cdr.markForCheck();
                      }
                    });
                  }
                }
              });
            }
          });

          this.isDirectApplyLoading = false;
        },
        error: () => {
          this.isDirectApplyLoading = false;
        }
      });
  }

  /**
   * Adds a college to DirectApply via POST /api/studentdirectapplication.
   * Pass the institute data and user to create the record.
   */
  addDirectApplyCollege(institute: Institute) {
    if (!this.user?.id) return;

    this.studentDirectApplicationService.addStudentDirectApplication({
      studentId: this.user.id,
      instituteId: institute.id,
      courseLevel: institute.courseLevel ?? null,
      courseId: institute.courseId ?? null,
      inTakeYear: institute.inTakeYear ?? ''
    }).subscribe({
      next: (response: any) => {
        const created = response?.result;
        if (created) {
          this.directApplicationId = created.id;

          this.messageService.add({
            severity: 'success',
            summary: 'Added',
            detail: `${institute.instituteName} added to Direct Apply!`
          });
          // Refresh the direct apply list using GET /{id}/myinstitutes
          this.loadDirectApplyColleges(created.id);
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add college to Direct Apply.'
        });
      }
    });
  }

  /**
   * Removes a college from DirectApply via DELETE /api/studentdirectapplication/{id}.
   * @param index — index in directApplyColleges array
   */
  removeDirectApply(index: number) {
    const item = this.directApplyColleges[index];
    if (!item?.id) {
      // If no API id, just remove from local list
      this.directApplyColleges.splice(index, 1);
      return;
    }

    this.studentDirectApplicationService.deleteStudentDirectApplication(item.id).subscribe({
      next: () => {
        this.directApplyColleges.splice(index, 1);
        this.messageService.add({
          severity: 'success',
          summary: 'Removed',
          detail: `${item.instituteName} removed from Direct Apply.`
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to remove college from Direct Apply.'
        });
      }
    });
  }

  get totalSlots(): number { return 5; }
  get hasPendingColleges(): boolean { return this.directApplyColleges.some(c => c.status === 0); }
  get selectedSlots(): number { return this.directApplyColleges.length; }
  get freeSlots(): number { return Math.max(0, this.totalSlots - this.selectedSlots); }
}
